import React, { useMemo, useState } from "react";
import { CTAButton, SectionLabel, Reveal, CountUp } from "@/lib/uiHelpers";
import { STAT_CARDS } from "@/constants/landingCopy";

// Mid-point of the advertised 80-90% range. Module-level so it's a stable
// reference and not a missing hook dependency.
const SAVINGS_RATE = 0.85;
const PROJECTION_YEARS = [10, 15, 20];

function currency(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function useSavingsProjections(bill) {
  return useMemo(() => {
    const yearly = bill * SAVINGS_RATE * 12;
    return PROJECTION_YEARS.map((years) => ({ years, total: Math.round(yearly * years) }));
  }, [bill]);
}

function StatCard({ card }) {
  return (
    <div
      className="h-full rounded-2xl bg-white p-7 text-center"
      style={{ borderTop: "4px solid #F5A623", boxShadow: "0 18px 40px -26px rgba(26,43,74,0.3)" }}
    >
      <div className="font-head font-extrabold text-5xl md:text-6xl" style={{ color: "#1A2B4A" }}>
        {card.countTo === 0 ? (
          card.value
        ) : (
          <CountUp end={card.countTo} prefix={card.prefix || ""} suffix={card.suffix || ""} />
        )}
      </div>
      <div className="font-head font-bold text-lg mt-2" style={{ color: "#2C3E50" }}>{card.label}</div>
      <p className="font-body text-sm mt-2" style={{ color: "#7F8C8D" }}>{card.sub}</p>
    </div>
  );
}

function ProjectionCard({ years, total, bill }) {
  return (
    <div className="rounded-2xl p-6 text-center" style={{ background: "#1A2B4A" }}>
      <div className="font-body text-sm uppercase tracking-wide" style={{ color: "#F5A623" }}>Over {years} Years</div>
      <div className="font-head font-extrabold text-3xl md:text-4xl mt-2" style={{ color: "#fff" }}>
        <CountUp key={`${years}-${bill}`} end={total} prefix="$" duration={900} />
      </div>
      <div className="font-body text-xs mt-2" style={{ color: "rgba(255,255,255,0.65)" }}>estimated savings kept</div>
    </div>
  );
}

function Calculator() {
  const [bill, setBill] = useState(350);
  const projections = useSavingsProjections(bill);

  return (
    <div
      className="rounded-3xl bg-white p-7 md:p-10"
      style={{ boxShadow: "0 24px 55px -30px rgba(26,43,74,0.4)", border: "1px solid #ece7dc" }}
    >
      <div className="text-center">
        <h3 className="font-head font-extrabold text-2xl md:text-3xl" style={{ color: "#1A2B4A" }}>Estimate What You&apos;d Keep</h3>
        <p className="font-body mt-2" style={{ color: "#7F8C8D" }}>
          Slide to your current monthly HECO bill &mdash; see what you could save, not what you&apos;d pay.
        </p>
      </div>

      <div className="mt-8 max-w-xl mx-auto">
        <div className="flex items-end justify-between mb-2">
          <label htmlFor="bill" className="font-body text-sm font-semibold" style={{ color: "#2C3E50" }}>Your monthly HECO bill</label>
          <span className="font-head font-extrabold text-2xl" style={{ color: "#F5A623" }}>{currency(bill)}</span>
        </div>
        <input
          id="bill"
          data-testid="bill-slider"
          type="range"
          min={100}
          max={1000}
          step={10}
          value={bill}
          onChange={(e) => setBill(Number(e.target.value))}
          className="w-full"
          style={{ accentColor: "#F5A623" }}
        />
        <div className="flex justify-between font-body text-xs mt-1" style={{ color: "#9aa4a6" }}>
          <span>$100</span>
          <span>$1,000+</span>
        </div>
      </div>

      <div className="mt-9 grid sm:grid-cols-3 gap-5">
        {projections.map((p) => (
          <ProjectionCard key={p.years} years={p.years} total={p.total} bill={bill} />
        ))}
      </div>

      <div className="mt-9 text-center">
        <CTAButton testId="calc-cta" variant="gold" className="w-full sm:w-auto">
          &rarr; Confirm My Savings &mdash; Book My Call
        </CTAButton>
      </div>
    </div>
  );
}

export default function SavingsNumbers() {
  return (
    <section className="section-pad" style={{ backgroundColor: "#F4F0E8" }}>
      <div className="container-max">
        <Reveal className="text-center max-w-2xl mx-auto">
          <SectionLabel>What This Means For You</SectionLabel>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl leading-tight" style={{ color: "#1A2B4A" }}>
            Here&apos;s What Maui Homeowners
            <span className="block">Are Keeping In Their Pocket</span>
          </h2>
          <p className="font-body mt-4 text-base md:text-lg" style={{ color: "#7F8C8D" }}>
            HECO rates have increased every single year. Every dollar you overpay is a dollar that
            could stay in your family&apos;s pocket. Here&apos;s what the program delivers:
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {STAT_CARDS.map((card, i) => (
            <Reveal key={card.label} delay={i * 0.1}>
              <StatCard card={card} />
            </Reveal>
          ))}
        </div>

        <p className="font-body text-xs md:text-sm italic text-center mt-6 max-w-3xl mx-auto" style={{ color: "#9aa4a6" }}>
          *Savings estimates are based on typical Maui residential usage and program eligibility.
          Actual savings vary by property. Your discovery call provides your personalized estimate.
        </p>

        <Reveal className="mt-12">
          <Calculator />
        </Reveal>
      </div>
    </section>
  );
}
